import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function TshirtArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Itaobuy Spreadsheet T-Shirt: Magliette Basic e Grafiche con Foto QC',
    description: 'Scopri le migliori T-Shirt su Itaobuy Spreadsheet: magliette basic in cotone, modelli grafici e streetwear. Guida italiana con foto QC e consigli per ogni stile.',
    author: { '@type': 'Organization', name: 'Itaobuy Spread' },
    datePublished: '2026-07-14',
    inLanguage: 'it',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Che tipo di T-Shirt posso trovare su Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Magliette basic in cotone, t-shirt grafiche con stampe, modelli streetwear, t-shirt oversize e canotte. Ogni articolo include foto QC per valutare la qualità.' } },
      { '@type': 'Question', name: 'Come riconosco una T-Shirt di qualità dalle foto QC?', acceptedAnswer: { '@type': 'Answer', text: 'Controlla la grammatura del cotone, la qualità delle cuciture su collo e spalle, la nitidezza della stampa e la consistenza del tessuto nelle foto ravvicinate.' } },
      { '@type': 'Question', name: 'Le taglie delle T-Shirt su Itaobuy Spreadsheet sono affidabili?', acceptedAnswer: { '@type': 'Answer', text: 'Consigliamo di confrontare sempre le misure fornite nelle descrizioni con le tue. Le foto QC possono aiutarti a capire la vestibilità reale del capo.' } },
    ],
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.mom';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Itaobuy Spreadsheet T-Shirt', item: `${siteUrl}/itaobuy-spreadsheet-t-shirt` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-20">
        <article className="px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-16">
          <nav className="text-xs text-warm-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-tealwarm-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-warm-600">Itaobuy Spreadsheet T-Shirt</span>
          </nav>

          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-tealwarm-100 text-tealwarm-700 text-xs font-semibold uppercase tracking-wide mb-4">
              Categoria T-Shirt
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-warm-950 leading-tight">
              <span className="text-tealwarm-600">Itaobuy Spreadsheet T-Shirt:</span> Magliette per Ogni Stile
            </h1>
            <p className="mt-4 text-sm md:text-base text-warm-600 leading-relaxed">
              La <strong>itaobuy spreadsheet t-shirt</strong> è la categoria perfetta per chi cerca magliette di qualità senza perdersi in ricerche infinite. Dai modelli basic in cotone pesante alle t-shirt grafiche con stampe originali, ogni prodotto è accompagnato da foto QC che mostrano tessuto, vestibilità e dettagli con trasparenza assoluta.
            </p>
          </header>

          <div className="mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Artfully%20arranged%20stack%20of%20premium%20cotton%20t-shirts%20in%20muted%20earth%20tones%20sand%20terracotta%20olive%20and%20cream%20folded%20on%20a%20light%20oak%20shelf%20with%20a%20trailing%20ivy%20plant%20beside%20it%2C%20soft%20morning%20light%20through%20a%20window%2C%20minimalist%20clothing%20boutique%20aesthetic%2C%20clean%20serene%20editorial%20product%20photography&width=1200&height=600&seq=article-tshirt-hero&orientation=landscape"
              alt="Itaobuy Spreadsheet T-Shirt magliette"
              title="Itaobuy Spreadsheet T-Shirt"
              className="w-full rounded-2xl"
            />
          </div>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Perché <span className="text-tealwarm-600">Itaobuy Spreadsheet</span> è ideale per le T-Shirt
            </h2>
            <p className="mt-4 text-sm md:text-base text-warm-700 leading-relaxed">
              Una maglietta può sembrare un acquisto semplice, ma la differenza tra un capo di qualità e uno scadente sta nei dettagli invisibili nelle foto promozionali. Con l&apos;<strong>itaobuy sheet</strong> hai accesso a foto QC che rivelano la vera grammatura del cotone, la precisione delle cuciture sul collo e sulle spalle, la qualità della stampa e la consistenza del tessuto. È come toccare il prodotto con mano prima di decidere.
            </p>
            <p className="mt-3 text-sm md:text-base text-warm-700 leading-relaxed">
              Che tu preferisca una t-shirt basic minimalista o una grafica con stampa audace, la categoria <strong>itaobuy spreadsheet t-shirt</strong> ha qualcosa per te. I prodotti sono organizzati per stile, prezzo e popolarità, così puoi trovare esattamente ciò che cerchi senza scroll infiniti.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Cosa controllare nelle <span className="text-tealwarm-600">foto QC</span>
            </h2>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-cream-50">
                    <th className="text-left py-3 px-4 font-semibold text-warm-900 rounded-l-xl">Dettaglio</th>
                    <th className="text-left py-3 px-4 font-semibold text-warm-900 rounded-r-xl">Cosa cercare</th>
                  </tr>
                </thead>
                <tbody className="text-warm-700">
                  <tr className="border-b border-cream-100"><td className="py-3 px-4 font-medium">Cucitura collo</td><td className="py-3 px-4">Doppia cucitura, nessun filo allentato, elastico uniforme</td></tr>
                  <tr className="border-b border-cream-100"><td className="py-3 px-4 font-medium">Grammatura cotone</td><td className="py-3 px-4">Tessuto spesso e opaco, non trasparente controluce</td></tr>
                  <tr className="border-b border-cream-100"><td className="py-3 px-4 font-medium">Stampa</td><td className="py-3 px-4">Colori vividi, bordi definiti, nessuna sbavatura</td></tr>
                  <tr><td className="py-3 px-4 font-medium">Etichette</td><td className="py-3 px-4">Etichetta interna ben cucita, informazioni complete</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-12">
            <div className="bg-tealwarm-50 rounded-2xl p-6 md:p-8">
              <h3 className="font-display text-xl font-bold text-warm-950 flex items-center gap-2">
                <i className="ri-lightbulb-line text-tealwarm-600"></i> Consiglio pratico
              </h3>
              <p className="mt-3 text-sm md:text-base text-warm-700 leading-relaxed">
                Quando navighi nella categoria <strong>itaobuy spreadsheet t-shirt</strong>, concentrati sempre sulle foto che mostrano il tessuto in controluce: questo è il test migliore per valutare la densità del cotone. Un buon tessuto sarà uniforme e opaco, mentre uno economico apparirà sottile e irregolare.
              </p>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">Domande frequenti</h2>
            <div className="mt-5 space-y-4">
              {[
                { q: 'Che tipo di T-Shirt posso trovare su Itaobuy Spreadsheet?', a: 'Magliette basic in cotone, t-shirt grafiche con stampe, modelli streetwear, t-shirt oversize e canotte. Ogni articolo include foto QC.' },
                { q: 'Come riconosco una T-Shirt di qualità dalle foto QC?', a: 'Controlla la grammatura, la qualità delle cuciture su collo e spalle, la nitidezza della stampa e la consistenza del tessuto nelle foto ravvicinate.' },
                { q: 'Le taglie delle T-Shirt su Itaobuy Spreadsheet sono affidabili?', a: 'Consigliamo di confrontare sempre le misure fornite con le tue. Le foto QC aiutano a capire la vestibilità reale.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.q}</h3>
                  <p className="mt-2 text-sm text-warm-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 bg-gradient-to-r from-warm-50 to-cream-50 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-warm-950">
              Trova la <span className="text-tealwarm-600">T-Shirt perfetta</span>
            </h2>
            <p className="mt-3 text-sm text-warm-600 max-w-md mx-auto">
              Esplora la categoria T-Shirt con foto QC e prodotti selezionati per ogni stile.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-all whitespace-nowrap cursor-pointer">
                T-Shirt su Itaobuy Sheet <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-warm-300 text-warm-800 text-sm font-semibold hover:bg-warm-50 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line"></i> Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}